import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Compass } from 'lucide-react';

import RestaurantOnboardingService from '../../../services/restaurant/RestaurantOnboardingService';
import { RestaurantOnboardingStatus } from '../../../types/restaurant/RestaurantOnboardingTypes';
import RestaurantOnboardingWizard from './RestaurantOnboardingWizard';

interface Props {
  branchId: number | null;
}

const dismissedKey = (branchId: number) =>
  `restaurant-onboarding-dismissed-${branchId}`;

const isDismissed = (branchId: number) => {
  try {
    return localStorage.getItem(dismissedKey(branchId)) === '1';
  } catch {
    return false;
  }
};

const RestaurantOnboardingHost = ({ branchId }: Props) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<RestaurantOnboardingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  const autoOpenedFor = useRef<number | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!branchId) return;
      opts?.silent ? setRefreshing(true) : setLoading(true);
      try {
        const response = await RestaurantOnboardingService.getStatus(branchId);
        setStatus(response.data);

        if (
          autoOpenedFor.current !== branchId &&
          !response.data.ready &&
          !isDismissed(branchId)
        ) {
          autoOpenedFor.current = branchId;
          setOpen(true);
        }
      } catch {
        // Silencioso: el onboarding es una ayuda, no debe romper el dashboard.
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [branchId]
  );

  useEffect(() => {
    setStatus(null);
    autoOpenedFor.current = null;
    void load();
  }, [load]);

  if (!branchId) return null;

  const handleNavigate = (route: string) => {
    setOpen(false);
    navigate(route);
  };

  const handleFinish = () => {
    setOpen(false);
    try {
      localStorage.setItem(dismissedKey(branchId), '1');
    } catch {
      /* noop */
    }
  };

  const pct = status?.percent ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-[50px] items-center gap-2 whitespace-nowrap rounded-lg border border-primary/30 bg-primary/5 px-4 text-sm font-medium text-primary transition hover:bg-primary hover:text-white"
      >
        <Compass className="h-4 w-4" />
        Guía de configuración
        {status && !status.ready && (
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold group-hover:bg-white/20">
            {pct}%
          </span>
        )}
      </button>

      {open && (
        <RestaurantOnboardingWizard
          status={status}
          loading={loading}
          refreshing={refreshing}
          onRefresh={() => load({ silent: true })}
          onNavigate={handleNavigate}
          onClose={() => setOpen(false)}
          onFinish={handleFinish}
        />
      )}
    </>
  );
};

export default RestaurantOnboardingHost;
